import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import { tenantHeaderRecord } from '@/lib/tenantHeaders'
import { appPath } from '@/lib/getPublicAppUrl'
import { uploadFileDirectToS3 } from '@/lib/fileUpload/directUpload'

export interface FileUploadRecord {
    id: string
    filename: string
    content_type: string
    created_at: string
}

export interface FileUploadListResponse {
    file_uploads: FileUploadRecord[]
}

export interface CreateFileUploadRequest {
    encoded_data: string
    filename: string
    content_type: string
    parent_type: string
}

export interface CreateDirectFileUploadRequest {
    file: File
    parent_type?: string
    parent_id?: string
    source_type?: string
    source_id?: string
}

export interface CreateFileUploadResponse {
    id: string
    filename: string
    content_type: string
    created_at: string
    state?: string
    ready?: boolean
}

export interface FileUploadStatusResponse {
    id: string
    content_type: string
    state?: string
    ready?: boolean
}

export interface FileUploadDownloadResponse {
    /** Present when the full download path is used; omitted for presigned_urls_only. */
    base64_encoded?: string
    content_type: string
    filename?: string
    /** Presigned S3 GET with inline Content-Disposition for in-browser preview. */
    preview_url?: string
    /** Presigned S3 GET with attachment Content-Disposition for native download. */
    download_url?: string
}

export interface FileDownloadViewModel {
    dataUrl: string
    contentType: string
    filename?: string
    downloadUrl?: string
}

function nileCoreApiOrigin(): string {
    return process.env.NEXT_PUBLIC_NILE_CORE_URL || 'http://localhost:3000'
}

/**
 * Download request URL for the patient file viewer.
 * When `presignedUrlsOnly` (tenant `directToS3FileUpload`), same-origin proxy +
 * Core skips base64. Otherwise the legacy cross-origin full download to Core.
 */
export function fileUploadDownloadUrl(fileId: string, presignedUrlsOnly: boolean): string {
    const path = apiEndpoints.fileUploads.download(fileId)
    if (presignedUrlsOnly) {
        return appPath(`${path}?presigned_urls_only=true`)
    }
    return `${nileCoreApiOrigin()}${path}`
}

export function toFileDownloadViewModel(data: FileUploadDownloadResponse): FileDownloadViewModel {
    const dataUrl = data.base64_encoded
        ? `data:${data.content_type};base64,${data.base64_encoded}`
        : data.preview_url || data.download_url || ''

    return {
        dataUrl,
        contentType: data.content_type,
        filename: data.filename,
        downloadUrl: data.download_url,
    }
}

async function fetchFileUploadDownload(
    fileId: string,
    { presignedUrlsOnly }: { presignedUrlsOnly: boolean },
): Promise<FileUploadDownloadResponse> {
    const response = await fetch(fileUploadDownloadUrl(fileId, presignedUrlsOnly), {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...tenantHeaderRecord() },
    })

    if (!response.ok) {
        throw Object.assign(new Error('file_download_failed'), { status: response.status })
    }

    return response.json() as Promise<FileUploadDownloadResponse>
}

// Legacy path: posts base64 directly to Nile-Core (Vercel caps proxied payloads at 4.5MB).
// Non-ok responses reject with the status so callers can refresh a stale session token on 401.
async function postFileUpload(body: CreateFileUploadRequest): Promise<CreateFileUploadResponse> {
    const response = await fetch(`${nileCoreApiOrigin()}${apiEndpoints.fileUploads.create}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...tenantHeaderRecord() },
        body: JSON.stringify(body),
    })

    if (!response.ok) {
        throw Object.assign(new Error('file_upload_failed'), { status: response.status })
    }

    return response.json() as Promise<CreateFileUploadResponse>
}

export const fileUploadApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        createFileUpload: builder.mutation<CreateFileUploadResponse, CreateFileUploadRequest>({
            async queryFn(body) {
                try {
                    return { data: await postFileUpload(body) }
                } catch (error) {
                    const status =
                        typeof error === 'object' && error !== null && 'status' in error
                            ? (error as { status: number }).status
                            : 'FETCH_ERROR'
                    return { error: { status, data: 'file_upload_failed' } }
                }
            },
        }),

        // Tenant capability path: same-origin JSON presign/complete; browser PUTs bytes to S3.
        createDirectFileUpload: builder.mutation<CreateFileUploadResponse, CreateDirectFileUploadRequest>({
            async queryFn({ file, parent_type, parent_id, source_type, source_id }) {
                try {
                    const data = await uploadFileDirectToS3(file, {
                        parent_type,
                        parent_id,
                        source_type,
                        source_id,
                    })
                    return { data }
                } catch (error) {
                    const status =
                        typeof error === 'object' && error !== null && 'status' in error
                            ? (error as { status: number }).status
                            : 'FETCH_ERROR'
                    return { error: { status, data: 'file_upload_failed' } }
                }
            },
        }),

        getFileUploadStatus: builder.query<FileUploadStatusResponse, { id: string }>({
            query: ({ id }) => ({ url: apiEndpoints.fileUploads.status(id) }),
        }),

        deleteFileUpload: builder.mutation<void, { id: string }>({
            query: ({ id }) => ({
                url: apiEndpoints.fileUploads.delete(id),
                method: 'DELETE',
            }),
        }),

        getMedicationCheckInLabResultUploads: builder.query<FileUploadListResponse, { sourceType: string }>({
            query: ({ sourceType }) => ({
                url: apiEndpoints.fileUploads.list,
                params: { source_type: sourceType },
            }),
            providesTags: ['MedicationCheckInLabUpload'],
        }),

        deleteMedicationCheckInLabResultUpload: builder.mutation<void, { id: string }>({
            query: ({ id }) => ({
                url: apiEndpoints.fileUploads.delete(id),
                method: 'DELETE',
            }),
            invalidatesTags: ['MedicationCheckInLabUpload'],
        }),

        getFileUploadDownload: builder.query<
            FileDownloadViewModel,
            { fileId: string; presignedUrlsOnly: boolean }
        >({
            async queryFn({ fileId, presignedUrlsOnly }) {
                try {
                    const data = await fetchFileUploadDownload(fileId, { presignedUrlsOnly })
                    return { data: toFileDownloadViewModel(data) }
                } catch (error) {
                    const status =
                        typeof error === 'object' && error !== null && 'status' in error
                            ? (error as { status: number }).status
                            : 'FETCH_ERROR'

                    return {
                        error: {
                            status,
                            data: error instanceof Error ? error.message : 'file_download_failed',
                        },
                    }
                }
            },
            providesTags: (_result, _error, { fileId }) => [{ type: 'FileUpload', id: fileId }],
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetMedicationCheckInLabResultUploadsQuery,
    useDeleteMedicationCheckInLabResultUploadMutation,
    useGetFileUploadDownloadQuery,
    useCreateFileUploadMutation,
    useCreateDirectFileUploadMutation,
    useLazyGetFileUploadStatusQuery,
    useDeleteFileUploadMutation,
} = fileUploadApi
